import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Switch, Button } from '@mui/material';
import { Card, Autocomplete, TextField, Typography } from '@totalsoft/rocket-ui';
import { AssignmentOutlined as AssignmentOutlinedIcon } from '@mui/icons-material';
import { DateTime } from '@ts-private/rocket-extensions';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set } from '@totalsoft/change-tracking-react';
import { emptyArray } from 'utils/constants';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';

export const InsuranceConventionGeneralDataComponent = ({
  lens,
  currencies,
  conventionTypes,
  trackingTypes,
  amountTypes,
  leasingInsuredAmountTypes,
  insuranceTypes,
  periodicities,
  isDirty,
  saving,
  onSave,
  validation
}) => {
  const [t] = useTranslation();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const currencyChange = useCallback(
    value => {
      set(lens.currencyId)(value.id);
      set(lens.currencyCode)(value.code);
    },
    [lens.currencyId, lens.currencyCode]
  );

  return (
    <Card icon={AssignmentOutlinedIcon} iconColor='primary' title={t('InsuranceConventions.GeneralData')}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='caption' fontWeight='bold'>
            {t('InsuranceConventions.ProductDescription')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            value={get(lens.productName) || ''}
            onChange={event => set(lens.productName)(event)}
            label={t('InsuranceConventions.Name')}
            error={!isValid(validation.productName)}
            helperText={getErrors(validation.productName)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            value={get(lens.productCode) || ''}
            onChange={event => set(lens.productCode)(event)}
            label={t('InsuranceConventions.Code')}
            error={!isValid(validation.productCode)}
            helperText={getErrors(validation.productCode)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DateTime
            disabled={!canWrite}
            name='startDate'
            timeFormat={false}
            value={get(lens.startDate) || ''}
            label={t('InsuranceConventions.StartDate')}
            onChange={event => set(lens.startDate)(event)}
            error={!isValid(validation.startDate)}
            helperText={getErrors(validation.startDate)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <DateTime
            disabled={!canWrite}
            name='endDate'
            timeFormat={false}
            value={get(lens.endDate) || ''}
            label={t('InsuranceConventions.EndDate')}
            onChange={event => set(lens.endDate)(event)}
            error={!isValid(validation.endDate)}
            helperText={getErrors(validation.endDate)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            required
            isSearchable={false}
            isClearable={false}
            valueKey='id'
            labelKey='code'
            value={{ id: lens.currencyId |> get, code: lens.currencyCode |> get }}
            onChange={currencyChange}
            options={currencies ? currencies : emptyArray}
            label={t('InsuranceConventions.Currency')}
            error={!isValid(validation.currencyId)}
            helperText={getErrors(validation.currencyId)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            disabled={!canWrite}
            control={<Switch checked={get(lens.isActive) || false} />}
            label={t('InsuranceConventions.IsActive')}
            onChange={event => set(lens.isActive)(event.target.checked)}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='caption' fontWeight='bold'>
            {t('InsuranceConventions.InsuranceDetails')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            value={lens.conventionTypeId |> get}
            onChange={lens.conventionTypeId |> set}
            simpleValue
            valueKey='id'
            labelKey='name'
            label={t('InsuranceConventions.InsurancePeriod')}
            options={conventionTypes}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            required
            value={lens.productPeriod |> get}
            onChange={lens.productPeriod |> set}
            simpleValue
            isMultiSelection={true}
            options={periodicities}
            label={t('InsuranceConventions.Periodicity')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            required
            simpleValue
            value={lens.insuranceAmountId |> get}
            onChange={lens.insuranceAmountId |> set}
            valueKey='id'
            labelKey='name'
            label={t('InsuranceConventions.InsurancePremium')}
            options={amountTypes}
            error={!isValid(validation.insuranceAmountId)}
            helperText={getErrors(validation.insuranceAmountId)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            isNumeric
            decimalScale={0}
            value={get(lens.defaultProductPeriod)}
            onChange={event => set(lens.defaultProductPeriod)(event)}
            label={t('InsuranceConventions.DefaultPeriodicity')}
            error={!isValid(validation.defaultProductPeriod)}
            helperText={getErrors(validation.defaultProductPeriod)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            value={lens.trackingTypeId |> get}
            onChange={lens.trackingTypeId |> set}
            simpleValue
            valueKey='id'
            labelKey='name'
            options={trackingTypes}
            label={t('InsuranceConventions.InsuranceTracking')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            value={lens.insuranceTypeId |> get}
            onChange={lens.insuranceTypeId |> set}
            simpleValue
            valueKey='id'
            labelKey='name'
            options={insuranceTypes}
            label={t('InsuranceConventions.InsuranceType')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            disabled={!canWrite}
            fullWidth
            required
            value={lens.insuredAmountTypeId |> get}
            onChange={lens.insuredAmountTypeId |> set}
            simpleValue
            valueKey='id'
            labelKey='name'
            options={leasingInsuredAmountTypes}
            label={t('InsuranceConventions.InsuredAmount')}
            error={!isValid(validation.insuredAmountTypeId)}
            helperText={getErrors(validation.insuredAmountTypeId)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            value={get(lens.amountTypeFormula) || ''}
            onChange={event => set(lens.amountTypeFormula)(event)}
            label={t('InsuranceConventions.FormulaDDP')}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            disabled={!canWrite}
            control={<Switch checked={get(lens.validateInsuredAmount) || false} />}
            label={t('InsuranceConventions.ValidateInsuredAmount')}
            onChange={event => set(lens.validateInsuredAmount)(event.target.checked)}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='caption' fontWeight='bold'>
            {t('InsuranceConventions.RoundUp')}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            isNumeric
            decimalScale={0}
            value={get(lens.roundInsuredAmount)}
            onChange={event => set(lens.roundInsuredAmount)(event)}
            label={t('InsuranceConventions.InsuredAmount')}
            error={!isValid(validation.roundInsuredAmount)}
            helperText={getErrors(validation.roundInsuredAmount)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            isNumeric
            decimalScale={0}
            value={get(lens.roundInsuranceAmount)}
            onChange={event => set(lens.roundInsuranceAmount)(event)}
            label={t('InsuranceConventions.InsurancePremium')}
            error={!isValid(validation.roundInsuranceAmount)}
            helperText={getErrors(validation.roundInsuranceAmount)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            disabled={!canWrite}
            fullWidth
            required
            isNumeric
            decimalScale={0}
            value={get(lens.roundInsuranceRate)}
            onChange={event => set(lens.roundInsuranceRate)(event)}
            label={t('InsuranceConventions.InsuranceRate')}
            error={!isValid(validation.roundInsuranceRate)}
            helperText={getErrors(validation.roundInsuranceRate)}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} pt={2} justifyContent='flex-end'>
        <Button color='primary' variant='contained' onClick={onSave} disabled={!isDirty || saving || !canWrite}>
          {saving ? t('Saving') : t('Save')}
        </Button>
      </Grid>
    </Card>
  );
};

InsuranceConventionGeneralDataComponent.propTypes = {
  lens: PropTypes.object.isRequired,
  currencies: PropTypes.array,
  conventionTypes: PropTypes.array,
  trackingTypes: PropTypes.array,
  amountTypes: PropTypes.array,
  leasingInsuredAmountTypes: PropTypes.array,
  insuranceTypes: PropTypes.array,
  periodicities: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired
};

export default InsuranceConventionGeneralDataComponent;
